/**
 * BRIDGE — module i18n catalogues and modal registrations (W2).
 *
 * The other of the two files allowed to import `modules/`. `shared/` reaches
 * modules through nothing but the registries this file fills.
 *
 * Called from `App.tsx` at module scope, before the first render, so no surface
 * can paint a raw `auth.login.title` key or fail to resolve a modal type.
 */
import { adminsModals } from '@/modules/Admins/config/admins-modals.config';
import { adminsCatalogs } from '@/modules/Admins/i18n';
import { authCatalogs } from '@/modules/Auth/i18n';
import { coursesCatalogs } from '@/modules/Courses/i18n';
import { dashboardCatalogs } from '@/modules/Dashboard/i18n';
import { meetingsCatalogs } from '@/modules/Meetings/i18n';
import { membersCatalogs } from '@/modules/Members/i18n';
import { integrationsModals } from '@/modules/Integrations/config/integrations-modals.config';
import { integrationsCatalogs } from '@/modules/Integrations/i18n';
import { moderationModals } from '@/modules/Moderation/config/moderation-modals.config';
import { notificationsModals } from '@/modules/Notifications/config/notifications-modals.config';
import { notificationsCatalogs } from '@/modules/Notifications/i18n';
import { moderationCatalogs } from '@/modules/Moderation/i18n';
import { legalModals } from '@/modules/Legal/config/legal-modals.config';
import { legalCatalogs } from '@/modules/Legal/i18n';
import { questionsModals } from '@/modules/Questions/config/questions-modals.config';
import { questionsCatalogs } from '@/modules/Questions/i18n';
import { settingsCatalogs } from '@/modules/Settings/i18n';
import { paymentsModals } from '@/modules/Payments/config/payments-modals.config';
import { paymentsCatalogs } from '@/modules/Payments/i18n';
import { subscriptionsModals } from '@/modules/Subscriptions/config/subscriptions-modals.config';
import { subscriptionsCatalogs } from '@/modules/Subscriptions/i18n';
import { trustModals } from '@/modules/Trust/config/trust-modals.config';
import { trustCatalogs } from '@/modules/Trust/i18n';
import { verificationModals } from '@/modules/Verification/config/verification-modals.config';
import { verificationCatalogs } from '@/modules/Verification/i18n';
import { violationsModals } from '@/modules/Violations/config/violations-modals.config';
import { violationsCatalogs } from '@/modules/Violations/i18n';
import { modalRegistry } from '@/shared/components/Modals/modal-registry/modal-registry';
import { accountModals } from '@/shared/components/account/config/account-modals.config';
import { registerCatalog } from '@/shared/i18n/register-catalog/register-catalog';

/**
 * The Overlay Lab is deliberately ABSENT from this bridge.
 *
 * It is a development-only workbench (P10.1), and a static import here would put
 * its catalogue and its two dialogs into the production main bundle even though
 * its route is never mounted — an `import.meta.env.DEV` guard folds away the
 * *use*, not the *import*. So the lab registers itself from its own route entry
 * (`routes/KitLab.tsx`), which lives inside the chunk that a production build
 * never emits. The one documented deviation from "the bridge is the only
 * registrar", and the reason is in that file.
 *
 * Namespace → catalogue, one line per module.
 */
const MODULE_CATALOGS = [
  { namespace: 'auth', catalogs: authCatalogs },
  { namespace: 'dashboard', catalogs: dashboardCatalogs },
  { namespace: 'admins', catalogs: adminsCatalogs },
  { namespace: 'members', catalogs: membersCatalogs },
  { namespace: 'settings', catalogs: settingsCatalogs },
  { namespace: 'integrations', catalogs: integrationsCatalogs },
  { namespace: 'questions', catalogs: questionsCatalogs },
  { namespace: 'legal', catalogs: legalCatalogs },
  { namespace: 'trust', catalogs: trustCatalogs },
  { namespace: 'verification', catalogs: verificationCatalogs },
  { namespace: 'moderation', catalogs: moderationCatalogs },
  // Its OWN namespace, not a section of `moderation`: reports and AI violations
  // are two queues with two permissions and two vocabularies, and a namespace
  // reaching into another module's is the i18n spelling of the import W2 forbids.
  { namespace: 'violations', catalogs: violationsCatalogs },
  // The category LABELS are deliberately not in here: the defaults grid and the
  // member preferences screen name the same eleven things, so they live in the
  // shared `common` catalogue (P17c's `stack-screen-keys` rule, one layer over).
  { namespace: 'notifications', catalogs: notificationsCatalogs },
  { namespace: 'courses', catalogs: coursesCatalogs },
  { namespace: 'meetings', catalogs: meetingsCatalogs },
  // Its OWN namespace, not a section of `settings`: the module owns the editors
  // for three settings keys (P10.4's hand-off) and the copy that explains a plan
  // mode is billing's sentence, not the settings screen's.
  { namespace: 'subscriptions', catalogs: subscriptionsCatalogs },
  { namespace: 'payments', catalogs: paymentsCatalogs },
] as const;

/** Every module's dialogs, one line per module. */
const MODULE_MODALS = [
  // The kit's own dialogs — the account lifecycle (P27c1) — registered beside
  // the modules': two modules open them, so neither may own them.
  ...accountModals,
  ...adminsModals,
  ...subscriptionsModals,
  ...paymentsModals,
  ...integrationsModals,
  ...questionsModals,
  ...legalModals,
  ...trustModals,
  ...verificationModals,
  ...moderationModals,
  ...violationsModals,
  ...notificationsModals,
];

export function initializeAppConfig(): void {
  for (const { namespace, catalogs } of MODULE_CATALOGS) {
    registerCatalog(namespace, catalogs);
  }

  // Modal registrations. A surface opens one of these by dispatching its TYPE
  // STRING, so nothing outside this bridge imports a dialog component (W2).
  for (const registration of MODULE_MODALS) {
    modalRegistry.register(registration);
  }
}
